import { useCallback, useEffect, useMemo, useState } from "react";
import { base, ecrireReglage, lireReglage, type Note } from "./base-locale";
import { preparerPhoto } from "./photo";
import { synchroniser, type ResultatSynchro } from "./synchronisation";

const formatHeure = new Intl.DateTimeFormat("fr-CH", { dateStyle: "short", timeStyle: "medium" });

function estInstallee(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

function tailleLisible(octets: number): string {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${(octets / 1024).toFixed(0)} Ko`;
  return `${(octets / 1024 / 1024).toFixed(1)} Mo`;
}

export function App() {
  const [code, setCode] = useState<string | undefined>();
  const [chargement, setChargement] = useState(true);
  const [enLigne, setEnLigne] = useState(navigator.onLine);
  const [notes, setNotes] = useState<Note[]>([]);
  const [derniereSynchro, setDerniereSynchro] = useState<ResultatSynchro | undefined>();
  const [infos, setInfos] = useState<{ persistant?: boolean; utilise?: number; serveur?: string; recues?: number }>({});

  const rafraichir = useCallback(async () => {
    setNotes(await base.notes.orderBy("creeLe").reverse().toArray());
  }, []);

  const lancerSynchro = useCallback(async () => {
    const resultat = await synchroniser();
    setDerniereSynchro(resultat);
    await rafraichir();
    if (navigator.onLine && code) {
      try {
        const reponse = await fetch("/api/proto/notes", { headers: { "X-Code-Acces": code } });
        if (reponse.ok) {
          const corps = (await reponse.json()) as { total: number; version: string };
          setInfos((i) => ({ ...i, recues: corps.total, serveur: corps.version }));
        }
      } catch {
        /* hors ligne : on réessaiera */
      }
    }
  }, [code, rafraichir]);

  // Démarrage : code d'accès, stockage persistant, liste des notes
  useEffect(() => {
    void (async () => {
      setCode(await lireReglage("code"));
      const persistant = (await navigator.storage?.persist?.()) ?? false;
      const estimation = await navigator.storage?.estimate?.();
      setInfos((i) => ({ ...i, persistant, ...(estimation?.usage !== undefined ? { utilise: estimation.usage } : {}) }));
      await rafraichir();
      setChargement(false);
    })();
  }, [rafraichir]);

  // Envoi automatique : au retour du réseau, au retour dans l'app, toutes les 20 secondes
  useEffect(() => {
    if (!code) return;
    const enLigneOui = () => { setEnLigne(true); void lancerSynchro(); };
    const enLigneNon = () => setEnLigne(false);
    const visible = () => { if (document.visibilityState === "visible") void lancerSynchro(); };
    window.addEventListener("online", enLigneOui);
    window.addEventListener("offline", enLigneNon);
    document.addEventListener("visibilitychange", visible);
    const minuterie = window.setInterval(() => void lancerSynchro(), 20_000);
    void lancerSynchro();
    return () => {
      window.removeEventListener("online", enLigneOui);
      window.removeEventListener("offline", enLigneNon);
      document.removeEventListener("visibilitychange", visible);
      window.clearInterval(minuterie);
    };
  }, [code, lancerSynchro]);

  const enAttente = notes.filter((n) => n.statut === "en_attente").length;

  if (chargement) return <main className="page" />;
  if (!code) return <EcranCode onValide={setCode} />;

  return (
    <main className="page">
      <header className="entete">
        <h1>noovum <span className="sous-titre">· essai hors ligne</span></h1>
        <div className={`etat ${enLigne ? "etat-ok" : "etat-hors"}`}>
          {enLigne ? "En ligne" : "Hors ligne"}
          {enAttente > 0 && <> · {enAttente} en attente</>}
        </div>
      </header>

      {!estInstallee() && (
        <p className="conseil">
          Pour installer l'app : bouton <strong>Partager</strong> de Safari, puis <strong>« Sur l'écran d'accueil »</strong>.
        </p>
      )}

      <NouvelleNote apresEnregistrement={async () => { await rafraichir(); void lancerSynchro(); }} />

      <section>
        <h2>Notes sur ce téléphone ({notes.length})</h2>
        {notes.length === 0 && <p className="discret">Aucune note pour l'instant.</p>}
        <ul className="liste">
          {notes.map((note) => <LigneNote key={note.id} note={note} />)}
        </ul>
      </section>

      <section className="infos">
        <h2>Contrôles</h2>
        <dl>
          <dt>Installée sur l'écran d'accueil</dt><dd>{estInstallee() ? "oui" : "non"}</dd>
          <dt>Stockage protégé contre l'effacement</dt><dd>{infos.persistant ? "oui" : "non (normal tant que l'app n'est pas installée)"}</dd>
          <dt>Espace utilisé sur le téléphone</dt><dd>{infos.utilise !== undefined ? tailleLisible(infos.utilise) : "—"}</dd>
          <dt>Notes reçues par le serveur</dt><dd>{infos.recues ?? "—"}</dd>
          <dt>Version du serveur</dt><dd>{infos.serveur ?? "—"}</dd>
          <dt>Dernier envoi</dt>
          <dd>
            {derniereSynchro
              ? `${derniereSynchro.envoyees} envoyée(s), ${derniereSynchro.restantes} en attente${derniereSynchro.probleme ? ` (${derniereSynchro.probleme})` : ""}`
              : "—"}
          </dd>
        </dl>
        <button className="bouton secondaire" onClick={() => void lancerSynchro()}>Envoyer maintenant</button>
      </section>
    </main>
  );
}

function EcranCode({ onValide }: { onValide: (code: string) => void }) {
  const [saisie, setSaisie] = useState("");
  const [message, setMessage] = useState<string | undefined>();

  const valider = async () => {
    if (!navigator.onLine) { setMessage("La première ouverture demande du réseau."); return; }
    const reponse = await fetch("/api/proto/verifier", { method: "POST", headers: { "X-Code-Acces": saisie.trim() } }).catch(() => undefined);
    if (reponse?.status === 204) {
      await ecrireReglage("code", saisie.trim());
      onValide(saisie.trim());
    } else {
      setMessage(reponse?.status === 401 ? "Code refusé." : "Serveur injoignable, réessayez.");
    }
  };

  return (
    <main className="page centre">
      <h1>noovum</h1>
      <p>Code d'accès de l'essai</p>
      <input className="champ" type="password" autoComplete="current-password" autoCapitalize="none"
        value={saisie} onChange={(e) => setSaisie(e.target.value)} />
      <button className="bouton" disabled={!saisie.trim()} onClick={() => void valider()}>Valider</button>
      {message && <p className="erreur">{message}</p>}
    </main>
  );
}

function NouvelleNote({ apresEnregistrement }: { apresEnregistrement: () => Promise<void> }) {
  const [texte, setTexte] = useState("");
  const [photo, setPhoto] = useState<Blob | undefined>();
  const [travail, setTravail] = useState(false);
  const apercu = useMemo(() => (photo ? URL.createObjectURL(photo) : undefined), [photo]);
  useEffect(() => () => { if (apercu) URL.revokeObjectURL(apercu); }, [apercu]);

  const enregistrer = async () => {
    setTravail(true);
    await base.notes.add({
      id: crypto.randomUUID(),
      texte: texte.trim(),
      ...(photo ? { photo } : {}),
      creeLe: new Date().toISOString(),
      statut: "en_attente",
    });
    setTexte("");
    setPhoto(undefined);
    setTravail(false);
    await apresEnregistrement();
  };

  return (
    <section className="carte">
      <h2>Nouvelle note</h2>
      <textarea className="champ" rows={3} placeholder="Exemple : salon, vitre gauche 822 × 1825"
        value={texte} onChange={(e) => setTexte(e.target.value)} />
      <label className="bouton secondaire">
        {photo ? "Changer la photo" : "Prendre une photo"}
        <input type="file" accept="image/*" capture="environment" hidden
          onChange={async (e) => { const f = e.target.files?.[0]; if (f) setPhoto(await preparerPhoto(f)); e.target.value = ""; }} />
      </label>
      {apercu && <img className="apercu" src={apercu} alt="Photo jointe" />}
      <button className="bouton" disabled={travail || (!texte.trim() && !photo)} onClick={() => void enregistrer()}>
        Enregistrer sur le téléphone
      </button>
    </section>
  );
}

function LigneNote({ note }: { note: Note }) {
  const vignette = useMemo(() => (note.photo ? URL.createObjectURL(note.photo) : undefined), [note.photo]);
  useEffect(() => () => { if (vignette) URL.revokeObjectURL(vignette); }, [vignette]);
  return (
    <li className="ligne">
      {vignette ? <img className="vignette" src={vignette} alt="" /> : <div className="vignette vide" />}
      <div>
        <div>{note.texte || <span className="discret">(photo seule)</span>}</div>
        <div className="discret">
          {formatHeure.format(new Date(note.creeLe))}
          {note.photo && <> · {tailleLisible(note.photo.size)}</>}
        </div>
        <div className={note.statut === "envoyee" ? "statut-ok" : "statut-attente"}>
          {note.statut === "envoyee"
            ? `Reçue par le serveur${note.envoyeeLe ? ` à ${formatHeure.format(new Date(note.envoyeeLe))}` : ""}`
            : "En attente d'envoi"}
        </div>
      </div>
    </li>
  );
}
